import * as Y from 'yjs';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Deck } from './modules/deck';
import { MultiPlayerBoardManager } from './modules/whiteboard';
import { yjsNetworkFactory } from './modules/yjs-networking';
import { getOrCreatePlayerId, getOrCreatePeerId } from './modules/yjs-networking';
import { Player } from './modules/player';
import { GameResourcesDock } from './modules/gameResourcesDock';
import { WelcomeModal, HotkeysModal, HelpModal, AddCardManager, PatchNotesModal, GameHotkeysManager, AnnouncementModal } from './components';
import { DeckManager } from './deck_manager';
import { OpponentHealthList } from './components/health/OpponentHealthList';
import { SavedDeck } from './modules/deck/types';
import { TokenService } from './services/scryfall';
import { ScryfallApiService } from '@/services/scryfall';
import { CardPreview } from './modules/cardPreview';
import { DeckStorageService } from './services/deckStorage';
import { DeckPersistenceService } from './services/deckPersistence';
import { RoomManager } from './services/roomManager';
import { WhiteboardEventHandlers } from './services/eventHandlers';
import { PatchNotesService } from './services/patchNotes';
import { DEFAULT_DECK } from './data/defaultDeck';
import './style.css';
import * as Sentry from "@sentry/react";
import posthog from 'posthog-js';
import {YSTATE_DECK_CARD_COUNT} from "./constants";
import {ReactToasterRoot} from "../ReactToasterRoot";
import {usePlayerStore} from "./stores/playerStore";
import {useGameInstance} from "./stores/gameInstanceStore";
import {RoomConnectionStatus} from "@/components/RoomConnectionStatus";
import {YjsNetworkProvider} from "@/modules/yjs-networking/YjsNetworkFactory";
import {AnnouncementsService} from "@/services/announcements/AnnouncementsService";
import {MobileWarningModal} from "@/components/MobileWarningModal";


posthog.init('phc_yVFqMSYG88kEXYf4vcMJgS7YuHpjRyYCD4aWicRXuJtF', {
  api_host: 'https://us.i.posthog.com',
  defaults: '2026-01-30',
});

Sentry.init({
  environment: process.env.NODE_ENV || "development",
  dsn: "https://beb5f109e66475063b4650877bc1c6a1@o4510353682006016.ingest.de.sentry.io/4510353685610576",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    })
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
  // Enable logs to be sent to Sentry
  enableLogs: true,
});

const baseUrl = "https://aura0.app/?room=";

const isDevEnv = import.meta.env.MODE === 'development';

const VISIT_COUNT_KEY = 'aura-visit-count';

class AuraApp {
  private yDoc: Y.Doc;
  private yjsNetworkProvider!: YjsNetworkProvider;
  private whiteboard!: MultiPlayerBoardManager;
  private localPlayer!: Player;
  private localDock!: GameResourcesDock;
  private opponentHealthRoot: Root | null = null;
  private gameHotkeysRoot: Root | null = null;
  private tokenService!: TokenService;
  private playerId: string;
  private scryfallApiService!: ScryfallApiService;
  private roomManager: RoomManager;
  private eventHandlers: WhiteboardEventHandlers | null = null;

  constructor() {
    this.yDoc = new Y.Doc();

    // Log size of Yjs incremental update. We want to eventually reduce the size and volume of updates
    // from drawing a card (70KB) and moving a card on board (hundreds of updates for a single drag).
    this.yDoc.on('update', (update, origin, doc) => {
      // `update` is a Uint8Array
      console.debug(`Yjs incremental update of size: ${update.byteLength} bytes`)
    })

    // Get or create persistent player ID (survives page reloads)
    this.playerId = getOrCreatePlayerId();
    console.log('Player ID:', this.playerId);

    // Initialize room manager (handles room ID and URL)
    this.roomManager = new RoomManager();
  }

  async initialize() {
    // Get or create persistent peer ID for WebRTC
    const peerId = getOrCreatePeerId();

    // Initialize WebRTC provider
    this.yjsNetworkProvider = await yjsNetworkFactory.create(this.yDoc, {
      roomName: this.roomManager.getRoomName(),
      peerId, // Pass persistent peer ID
    });

    // Initialize local player deck - restore from localStorage if available for this room
    const restoredDeck: Deck | null = DeckPersistenceService.restoreDeckForRoom(this.roomManager.getRoomName());
    const localDeck: Deck | null = restoredDeck;

    // Initialize local player
    this.localPlayer = new Player(this.playerId, this.yDoc, localDeck, {
      initialHealth: 40,
    });

    // Initialize Zustand store with yPlayerState for global access
    usePlayerStore.getState().setYPlayerState(this.localPlayer.yPlayerState);

    // Initialize multi-player board manager
    const whiteboardContainer = document.getElementById('whiteboard');
    if (!whiteboardContainer) {
      throw new Error('Whiteboard container not found');
    }

    this.whiteboard = new MultiPlayerBoardManager(
      whiteboardContainer,
      this.yDoc,
      this.playerId,
      '#1a1a1a', // backgroundColor
    );

    // Initialize local player's resource dock
    const dockContainer = document.getElementById('local-dock');
    if (!dockContainer) {
      throw new Error('Local dock container not found');
    }

    this.localDock = new GameResourcesDock(dockContainer, this.localPlayer, {
      position: 'bottom',
      playerId: this.playerId,
    }, this.whiteboard.getTooltipManager());

    // init toaster for alerts like "Opponent revealed deck"
    const toasterContainer = document.getElementById("toaster-root");
    if (toasterContainer) {
      const toasterRoot = createRoot(toasterContainer);
      toasterRoot.render(
        React.createElement(ReactToasterRoot)
      );
    }

    // Initialize opponent health display with React
    const opponentHealthContainer = document.getElementById('opponent-health-container');
    if (!opponentHealthContainer) {
      throw new Error('Opponent health container not found');
    }

    this.opponentHealthRoot = createRoot(opponentHealthContainer);
    this.opponentHealthRoot.render(
      React.createElement(OpponentHealthList, {
        yDoc: this.yDoc,
        localPlayerId: this.playerId,
      })
    );

    // Initialize Scryfall API service
    this.scryfallApiService = new ScryfallApiService();

    // Initialize token service with zoom level provider
    this.tokenService = new TokenService(
      () => this.whiteboard.getZoomLevel(), // Inject zoom level getter
      this.scryfallApiService,
    );

    this.setupEventListeners();
    this.setupConnectionStatus();
    this.setupAllHotkeys();
    this.setupDeckManager();
    this.setupHelpModal();
    this.setupDiscordButton();
    this.setupHotkeyHintsModal();
    this.setupAddCardModal();
    this.trackVisitCount();
  }


  private setupAllHotkeys(): void {
    // Populate the game instance store for the hotkeys hook
    useGameInstance.getState().setPlayer(this.localPlayer);
    useGameInstance.getState().setWhiteboard(this.whiteboard);
    useGameInstance.getState().setCardPreview(new CardPreview());
    useGameInstance.getState().setPlayerId(this.playerId);
    useGameInstance.getState().setRoomManager(this.roomManager);

    // Create a container for the GameHotkeysManager component
    const gameHotkeysContainer = document.createElement('div');
    gameHotkeysContainer.id = 'game-hotkeys-manager';
    document.body.appendChild(gameHotkeysContainer);

    this.gameHotkeysRoot = createRoot(gameHotkeysContainer);
    this.gameHotkeysRoot.render(React.createElement(GameHotkeysManager));
  }

  private setupEventListeners(): void {
    // Initialize event handlers for whiteboard interactions
    this.eventHandlers = new WhiteboardEventHandlers(
      this.yDoc,
      this.localPlayer,
      this.whiteboard,
      this.tokenService,
      this.playerId,
      () => DeckPersistenceService.saveDeckForRoom(this.roomManager.getRoomName(), this.localPlayer.getDeck())
    );
    this.eventHandlers.setupEventListeners();

    // Setup event listeners for battlefield card movements (from hotkeys)
    window.addEventListener('moveCardToHand', (e: Event) => {
      const customEvent = e as CustomEvent;
      const card = customEvent.detail.card;
      const hand = this.localPlayer.getState().hand;
      this.localPlayer['yPlayerState'].set('hand', [...hand, card]);
    });

    window.addEventListener('moveCardToDiscard', (e: Event) => {
      const customEvent = e as CustomEvent;
      const card = customEvent.detail.card;
      this.localPlayer.placeCardInPile(card, 'discard');
    });

    window.addEventListener('moveCardToExile', (e: Event) => {
      const customEvent = e as CustomEvent;
      const card = customEvent.detail.card;
      this.localPlayer.placeCardInPile(card, 'exile');
    });

    window.addEventListener('moveCardToDeckTop', (e: Event) => {
      const customEvent = e as CustomEvent;
      const card = customEvent.detail.card;
      this.localPlayer.moveCardToDeckTop(card);
      DeckPersistenceService.saveDeckForRoom(this.roomManager.getRoomName(), this.localPlayer.getDeck());
    });

    window.addEventListener('moveCardToDeckBottom', (e: Event) => {
      const customEvent = e as CustomEvent;
      const card = customEvent.detail.card;
      this.localPlayer.moveCardToDeckBottom(card);
      DeckPersistenceService.saveDeckForRoom(this.roomManager.getRoomName(), this.localPlayer.getDeck());
    });
  }

  private setupConnectionStatus(): void {
    const statusElement = document.getElementById("connection-status")!;
    const roomElement = document.getElementById("room-name");
    const copySVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M9.116 17q-.691 0-1.153-.462T7.5 15.385V4.615q0-.69.463-1.153T9.116 3h7.769q.69 0 1.153.462t.462 1.153v10.77q0 .69-.462 1.152T16.884 17zm0-1h7.769q.23 0 .423-.192t.192-.423V4.615q0-.23-.192-.423T16.884 4H9.116q-.231 0-.424.192t-.192.423v10.77q0 .23.192.423t.423.192m-3 4q-.69 0-1.153-.462T4.5 18.385V6.615h1v11.77q0 .23.192.423t.423.192h8.77v1zM8.5 16V4z"/></svg>`;
    const checkSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="m10.562 15.908l6.396-6.396l-.708-.708l-5.688 5.688l-2.85-2.85l-.708.708zM12.003 21q-1.866 0-3.51-.708q-1.643-.709-2.859-1.924t-1.925-2.856T3 12.003t.709-3.51Q4.417 6.85 5.63 5.634t2.857-1.925T11.997 3t3.51.709q1.643.708 2.859 1.922t1.925 2.857t.709 3.509t-.708 3.51t-1.924 2.859t-2.856 1.925t-3.509.709M12 20q3.35 0 5.675-2.325T20 12t-2.325-5.675T12 4T6.325 6.325T4 12t2.325 5.675T12 20m0-8"/></svg>`;

    if (roomElement) {
      roomElement.innerHTML = `COPY GAME LINK ${copySVG}`;
      roomElement.addEventListener("click", (event) => {
        event.preventDefault();
        navigator.clipboard
          .writeText(window.location.href)
          .then(() => {
            posthog.capture('room_link_copied', { room: this.roomManager.getRoomName() });
            roomElement.innerHTML = `COPIED! ${checkSVG}`;
            roomElement.style.color = '#4ade80';
            setTimeout(() => {
              roomElement.innerHTML = `COPY GAME LINK ${copySVG}`;
              roomElement.style.color = '#b1b5c5';
            }, 2000);
          });
      });
    }

    const statusRoot = createRoot(statusElement);
    statusRoot.render(React.createElement(RoomConnectionStatus, { yjsNetworkProvider: this.yjsNetworkProvider }));
  }

  private async setupDeckManager(): Promise<void> {
    const deckManagerRoot = document.getElementById('deck-manager-root');
    if (!deckManagerRoot) {
      throw new Error('Deck manager root not found');
    }

    const storage = new DeckStorageService();

    // Check if this is the user's first-ever load
    const FIRST_LOAD_KEY = 'aura-first-load-completed';
    const hasLoadedBefore = localStorage.getItem(FIRST_LOAD_KEY);

    if (!hasLoadedBefore || isDevEnv) {
      // First load ever - add default deck if no decks exist
      // ---
      // isDevEnv will force this statement to load decks during
      // Playwright testing. Without it, the browser won't load
      // the default deck for some reason.

      const deckCount = await storage.getDeckCount();

      if (deckCount === 0) {
        await storage.saveDeck(DEFAULT_DECK);
        console.log('Default deck added on first load');
      }

      // Mark that first load is complete
      localStorage.setItem(FIRST_LOAD_KEY, 'true');
    }

    await this.loadDeckOnStart(storage);

    const root = createRoot(deckManagerRoot);
    root.render(
      React.createElement(DeckManager, {
        onDeckSelected: (deck: SavedDeck) => this.loadDeck(deck),
      })
    );

    // Setup welcome modal
    const mobileWarningDiv = document.createElement('div');
    mobileWarningDiv.id = 'mobile-warning-root';
    document.body.appendChild(mobileWarningDiv);
    const mobileWarningRoot = createRoot(mobileWarningDiv);
    mobileWarningRoot.render(React.createElement(MobileWarningModal));

    // Setup welcome modal
    const welcomeModalRoot = document.createElement('div');
    welcomeModalRoot.id = 'welcome-modal-root';
    document.body.appendChild(welcomeModalRoot);
    const welcomeRoot = createRoot(welcomeModalRoot);

    this.setupAnnouncementsModal();
    if (isDevEnv) return;  // don't show modals, for Playwright testing
    welcomeRoot.render(React.createElement(WelcomeModal));

    // we haven't been making many changes, disabling patch notes because
    // there's some weird bug where patch notes keep showing in prod
    // this.setupPatchNotesModal();
    this.setupAnnouncementsModal();
  }

  private async loadDeckOnStart(storage: DeckStorageService) {
    // Only auto-load deck when entering a NEW room, not when reconnecting
    const isRecentRoom = this.roomManager.isRecentRoom();
    if (isRecentRoom) {
      console.log('Reconnecting to recent room - skipping auto-load to preserve game state');
      return;
    }

    // Mark this room as visited
    this.roomManager.markRoomAsVisited();
    posthog.capture('game_session_started', { room: this.roomManager.getRoomName() });
    console.log('New room detected - will auto-load deck');

    // Auto-load the first available deck on entering a new room
    const LAST_LOADED_DECK_KEY = 'aura-last-loaded-deck';
    const lastLoadedDeckId = localStorage.getItem(LAST_LOADED_DECK_KEY);

    try {
      let deckToLoad: SavedDeck | null = null;

      // Try to load the last loaded deck
      if (lastLoadedDeckId) {
        deckToLoad = await storage.getDeck(lastLoadedDeckId);
      }

      // If no last loaded deck or it doesn't exist anymore, get the first available deck
      if (!deckToLoad) {
        const allDecks = await storage.getAllDecks();
        if (allDecks.length > 0) {
          // Sort by last modified (most recent first) and take the first one
          allDecks.sort((a, b) =>
            new Date(b.metadata.lastModified).getTime() - new Date(a.metadata.lastModified).getTime()
          );
          deckToLoad = allDecks[0];
        }
      }

      // Load the deck if found
      if (deckToLoad) {
        this.loadDeck(deckToLoad);
        localStorage.setItem(LAST_LOADED_DECK_KEY, deckToLoad.metadata.id);
        console.log(`Auto-loaded deck "${deckToLoad.metadata.name}" for new room`);
      }
    } catch (error) {
      console.error('Error auto-loading deck:', error);
      // Continue without loading a deck - user can manually select one
    }
  }

  private loadDeck(savedDeck: SavedDeck): void {
    console.log(`Loading deck: ${savedDeck.metadata.name} (${savedDeck.cards.length} cards)`);

    // Reset player state: move all cards back to deck, clear piles, reset health
    this.localPlayer.reset();

    // Update the player state with deck
    this.localPlayer.loadNewDeck(savedDeck).then(() => {
      // Update deck count in Yjs state
      this.localPlayer['yPlayerState'].set(YSTATE_DECK_CARD_COUNT, this.localPlayer.getDeck().getCardCount());

      // Save this as the last loaded deck for auto-loading on next visit
      localStorage.setItem('aura-last-loaded-deck', savedDeck.metadata.id);

      // Save the deck state for this room so it persists on refresh
      DeckPersistenceService.saveDeckForRoom(this.roomManager.getRoomName(), this.localPlayer.getDeck());

      posthog.capture('deck_loaded', {
        deck_name: savedDeck.metadata.name,
        card_count: savedDeck.cards.length,
        deck_format: savedDeck.metadata.format,
        room: this.roomManager.getRoomName(),
      });
      console.log(`Deck "${savedDeck.metadata.name}" loaded successfully!`);
    });
  }

  private setupHelpModal(): void {
    const helpRoot = document.getElementById('help-root');
    if (!helpRoot) {
      throw new Error('Help root not found');
    }

    // Create a simple component that manages the button and modal state
    const HelpButton: React.FC = () => {
      const [isOpen, setIsOpen] = React.useState(false);

      return React.createElement(
        React.Fragment,
        null,
        React.createElement(
          'button',
          {
            className: 'toolbar-button',
            onClick: () => setIsOpen(true),
          },
          'Help'
        ),
        React.createElement(HelpModal, {
          isOpen,
          onClose: () => setIsOpen(false),
        })
      );
    };

    const root = createRoot(helpRoot);
    root.render(React.createElement(HelpButton));
  }

  private setupDiscordButton(): void {
    const discordRoot = document.getElementById('discord-root');
    if (!discordRoot) {
      throw new Error('Discord root not found');
    }

    const DiscordButton: React.FC = () => {
      return React.createElement(
        'button',
        {
          className: 'toolbar-button discord',
          onClick: () => window.open('https://discord.gg/PgH2gVZYKq', '_blank'),
          'aria-label': 'Join Discord Server',
        },
        React.createElement('img', {
          src: '/assets/Discord-Logo-White.svg',
          alt: 'Discord',
          style: { height: '16px' },
        })
      );
    };

    const root = createRoot(discordRoot);
    root.render(React.createElement(DiscordButton));
  }

  private setupHotkeyHintsModal(): void {
    const hotkeysRoot = document.getElementById('hotkeys-root');
    if (!hotkeysRoot) {
      throw new Error('Hotkeys root not found');
    }

    // Create a simple component that manages the button and modal state
    const HotkeysButton: React.FC = () => {
      const [isOpen, setIsOpen] = React.useState(false);

      return React.createElement(
        React.Fragment,
        null,
        React.createElement(
          'button',
          {
            className: 'toolbar-button',
            onClick: () => setIsOpen(true),
          },
          'Hotkeys'
        ),
        React.createElement(HotkeysModal, {
          isOpen,
          onClose: () => setIsOpen(false),
        })
      );
    };

    const root = createRoot(hotkeysRoot);
    root.render(React.createElement(HotkeysButton));
  }

  private setupAddCardModal(): void {
    const addCardModalRoot = document.createElement('div');
    addCardModalRoot.id = 'add-card-modal-root';
    document.body.appendChild(addCardModalRoot);

    const root = createRoot(addCardModalRoot);
    root.render(
      React.createElement(AddCardManager, {
        scryfallApiService: this.scryfallApiService,
        onAddCard: (card) => this.localPlayer.placeCardInPile(card, 'hand'),
      })
    );
  }

  private setupPatchNotesModal(): void {
    // Only show patch notes if there are new updates
    if (!PatchNotesService.shouldShowPatchNotes()) {
      console.log('No new patch notes to show');
      return;
    }

    const patchNotesModalRoot = document.createElement('div');
    document.body.appendChild(patchNotesModalRoot);

    const root = createRoot(patchNotesModalRoot);
    root.render(
      React.createElement(PatchNotesModal, {
        onClose: () => PatchNotesService.markPatchNotesAsSeen(),
      })
    );
  }

  private setupAnnouncementsModal(): void {
    // Only show announcements if there are new updates
    if (!AnnouncementsService.shouldShowAnnouncement()) {
      console.log('No new announcements to show');
      return;
    }

    console.log('Announcements to show');

    const announcementsModalRoot = document.createElement('div');
    document.body.appendChild(announcementsModalRoot);

    const root = createRoot(announcementsModalRoot);
    root.render(
      React.createElement(AnnouncementModal, {
        onClose: () => AnnouncementsService.markAnnouncementAsSeen(),
      })
    );
  }

  private trackVisitCount(): void {
    // Track visit count
    const visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    const newVisitCount = visitCount + 1;
    localStorage.setItem(VISIT_COUNT_KEY, newVisitCount.toString());
  }
}

// Initialize the app
const app = new AuraApp();
app.initialize().catch(error => {
  console.error('Failed to initialize app:', error);
  Sentry.captureException(error);
});
